/**
 * The composer's typed-in suggestion (the room's opening question): a pure typing
 * schedule plus the hook that plays it.
 *
 * The suggestion types in fast as the placeholder when it first arrives, so it reads
 * as freshly written for this room without making anyone wait: 20ms a character,
 * capped so a long question still lands within `SUGGESTION_MAX_TYPING_MS`. Reduced
 * motion, or a composer the user is already typing in, shows it whole at once.
 */

import { useEffect, useRef, useState } from 'react'

export const SUGGESTION_MS_PER_CHAR = 20
export const SUGGESTION_MAX_TYPING_MS = 1200
export const SUGGESTION_TICK_MS = 20

/** How many characters of a suggestion of `length` show `elapsedMs` after typing began. */
export function getTypedSuggestionLength(elapsedMs: number, length: number, prefersReducedMotion: boolean): number {
  if (length <= 0) {
    return 0
  }
  if (prefersReducedMotion) {
    return length
  }
  const msPerChar = Math.min(SUGGESTION_MS_PER_CHAR, SUGGESTION_MAX_TYPING_MS / length)
  const typed = Math.floor(Math.max(0, elapsedMs) / msPerChar)
  return Math.min(length, typed)
}

/**
 * Read at play time, not through a hook: every composer renders this hook, and only
 * one with a suggestion needs to know.
 */
function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  } catch {
    return false
  }
}

/**
 * The part of `suggestion` typed in so far. A new suggestion types from the start;
 * one that arrives while the composer holds text (or under reduced motion) shows
 * whole, since nobody sees the placeholder then.
 */
export function useTypedSuggestion(suggestion: string | null, isComposerEmpty: boolean): string {
  const [typedLength, setTypedLength] = useState(0)
  const playedRef = useRef<string | null>(null)
  const isComposerEmptyRef = useRef(isComposerEmpty)
  isComposerEmptyRef.current = isComposerEmpty

  useEffect(() => {
    if (!suggestion) {
      playedRef.current = null
      setTypedLength(0)
      return
    }
    if (playedRef.current === suggestion) {
      return
    }
    playedRef.current = suggestion
    const length = suggestion.length
    if (prefersReducedMotion() || !isComposerEmptyRef.current) {
      setTypedLength(length)
      return
    }
    setTypedLength(0)
    const startedAt = Date.now()
    const timer = setInterval(() => {
      const next = getTypedSuggestionLength(Date.now() - startedAt, length, false)
      setTypedLength(next)
      if (next >= length) {
        clearInterval(timer)
      }
    }, SUGGESTION_TICK_MS)
    return () => {
      clearInterval(timer)
      // An interrupted run types again from the start when the effect re-runs (StrictMode, a newer suggestion).
      playedRef.current = null
    }
  }, [suggestion])

  return suggestion ? suggestion.slice(0, typedLength) : ''
}
